import { track } from "@amplitude/analytics-react-native";
import { useCallback, useMemo, useState } from "react";
import {
	Gesture,
	GestureStateChangeEvent,
	GestureUpdateEvent,
	PanGestureChangeEventPayload,
	PanGestureHandlerEventPayload,
} from "react-native-gesture-handler";
import {
	runOnJS,
	SharedValue,
	useAnimatedReaction,
	useSharedValue,
	withDecay,
} from "react-native-reanimated";
import haptic from "src/helpers/haptic";

const HapticFrequency = Math.PI;
const HapticOffset = Math.PI / 2;

type DimSV = SharedValue<{ width: number; height: number }>;
const useSpinGesture = ({
	gestureDetectorDim,
	verticalScrollEnablerDim,
	hasHaptics = true,
}: {
	gestureDetectorDim: DimSV;
	/**
	 * An additional gesture detector used to allow for vertical scroll by only detecting horizontal movement.
	 */
	verticalScrollEnablerDim?: DimSV;
	hasHaptics?: boolean;
}) => {
	const theta = useSharedValue(0);
	const initialTheta = useSharedValue(0);
	const [isMoving, setIsMoving] = useState(false);

	const createHandlers = useCallback(
		(dim: DimSV) => {
			const handleOnBegin = () => {
				track("wheel_spin_begin");
			};
			const handleOnEnd = () => {
				track("wheel_spin_end");
			};
			const handleBegin = (event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => {
				"worklet";
				runOnJS(setIsMoving)(true);
				runOnJS(handleOnBegin)();
				initialTheta.value =
					Math.atan2(event.y - dim.value.height / 2, event.x - dim.value.width / 2) -
					theta.value;
			};
			const handleChange = (
				event: GestureUpdateEvent<
					PanGestureHandlerEventPayload & PanGestureChangeEventPayload
				>,
			) => {
				"worklet";
				theta.value =
					Math.atan2(event.y - dim.value.height / 2, event.x - dim.value.width / 2) -
					initialTheta.value;
			};
			const handleEnd = (event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => {
				"worklet";
				const x = event.x - dim.value.width / 2;
				const y = event.y - dim.value.height / 2;
				const magnitudeSquared = x ** 2 + y ** 2;
				if (magnitudeSquared === 0) return;
				runOnJS(handleOnEnd)();

				const dotProduct = (-y * event.velocityX + x * event.velocityY) / magnitudeSquared;

				theta.value = withDecay(
					{
						velocity: dotProduct,
						deceleration: 0.9987,
					},
					() => {
						"worklet";
						runOnJS(setIsMoving)(false);
					},
				);
			};
			return {
				handleBegin,
				handleChange,
				handleEnd,
			};
		},
		[theta, initialTheta],
	);

	useAnimatedReaction(
		() => (hasHaptics && isMoving ? theta.value : undefined),
		(result, prev) => {
			if (result == null || prev == null) return;
			const floor = Math.floor(result / HapticFrequency + HapticOffset);
			const prevFloor = Math.floor(prev / HapticFrequency + HapticOffset);
			if (floor !== prevFloor) {
				runOnJS(haptic)("Light");
			}
		},
		[hasHaptics, theta, isMoving],
	);

	const spinGesture = useMemo(() => {
		const { handleBegin, handleChange, handleEnd } = createHandlers(gestureDetectorDim);

		const gesture = Gesture.Pan()
			.onBegin(handleBegin)
			.onChange(handleChange)
			.onEnd(handleEnd)
			.minDistance(0);
		return gesture;
	}, [createHandlers, gestureDetectorDim]);
	const scrollEnablerGesture = useMemo(() => {
		if (!verticalScrollEnablerDim) return null;
		const { handleBegin, handleChange, handleEnd } = createHandlers(verticalScrollEnablerDim);

		const gesture = Gesture.Pan()
			.onBegin(handleBegin)
			.onChange(handleChange)
			.onEnd(handleEnd)
			// Needs to move at least 10 pixels in the x direction to activate
			.activeOffsetX([-10, 10])
			// But if it moves more than 10 pixels in the y direction before, it fails
			.failOffsetY([-10, 10]);
		return gesture;
	}, [createHandlers, verticalScrollEnablerDim]);

	return {
		theta,
		spinGesture,
		scrollEnablerGesture,
		isMoving,
	};
};
export default useSpinGesture;
